import { useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../services/api';
import { onAngularEvent, trackEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface BillingCycle {
  totalProjectSize: number;
  processingBytes: number;
  hostingByByteDays: number;
  rollingHostingBytes: number;
  nativeExportsBytes: number;
  imageExportsPageCount: number;
  translatedCharacterCount: number;
  transcriptionSeconds: number;
  dataEnrichmentRequestCount: number;
  glinerNlpDocumentCount: number;
  dailyStatistics: {
    date: number;
    processingBytes: number | null;
    rollingHostingBytes: number | null;
    convertedBytes: number | null;
    nativeExportsBytes: number | null;
    imageExportsPageCount: number | null;
    translatedCharacterCount: number | null;
    transcriptionSeconds: number | null;
    glinerNlpDocumentCount: number | null;
  }[];
  period: {
    startDate: number;
    endDate: number;
  };
}

export function useBilling(projectId: string, startDate?: string, endDate?: string) {
  const path = startDate && endDate
    ? `/billing/project/${projectId}/start/${startDate}/end/${endDate}`
    : `/billing/project/${projectId}`;
  return useQuery<BillingCycle>({
    queryKey: ['billing', projectId, startDate, endDate],
    queryFn: () => api.get<BillingCycle>(path),
    enabled: !!projectId,
  });
}

export function useGenerateBillingReport(projectId: string) {
  useEffect(() => {
    return onAngularEvent('FeedService.BillingReportComplete', (url: string) => {
      notifySuccess({
        title: 'Report available',
        description: 'Billing Report has completed.',
        buttons: `<a class="btn btn-success" href="${url}"><i class="icon-download-alt"></i> Download</a>`,
      });
    });
  }, [projectId]);

  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      trackEvent('download billing report', 'clicked', `${startDate} to ${endDate}`);
      return api.get(`/billing/csv/project/${projectId}/start/${startDate}/end/${endDate}`);
    },
  });
}

export function billingCsvUrl(projectId: string, startDate: string, endDate: string, baseUrl: string): string {
  return `${baseUrl}/billing/csv/project/${projectId}/start/${startDate}/end/${endDate}`;
}
